import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { User, Mail, Lock, Save, Trash2, Loader2, Crown, CalendarClock, Receipt, CheckCircle2, Clock, Download, RotateCcw, AlertTriangle, Coins, Bell, AtSign, MonitorSmartphone, History, MapPin, KeyRound, Camera, ShieldCheck } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from './ui/alert-dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { useAuth } from '../auth';
import { useLang } from '../i18n';
import api from '../api';

import { PointsHistorySection } from './AccountSections';
import { LoginMap } from './LoginMap';
import { UsernameAvailability } from './UsernameAvailability';
import { InitialsAvatar, PALETTE } from './InitialsAvatar';
import { AvatarCropDialog } from './AvatarCropDialog';

// Convert a two-letter ISO country code (e.g. "DE") into its flag emoji by
// mapping each letter to its Regional Indicator Symbol. Returns '' when invalid.
const flagEmoji = (code) => {
  if (!code || typeof code !== 'string' || code.length !== 2) return '';
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
};

export const AccountSettingsDialog = ({ open, onClose }) => {
  const { user, updateUser, logout, isPaid, entitlement, cancelSubscription, reactivateSubscription, openPricing, refresh, redeemLicense } = useAuth();
  const { lang } = useLang();
  const en = lang === 'en';
  const t = (de, eng) => (en ? eng : de);
  const isPasswordAccount = (user?.auth_provider || 'password') === 'password';
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const pendingDeletion = !!user?.pending_deletion;

  const cancelAtPeriodEnd = !!entitlement?.cancel_at_period_end;
  const periodEndIso = entitlement?.current_period_end || entitlement?.trial_end || null;
  const isLifetime = entitlement?.plan === 'lifetime';
  const daysUntilEnd = (() => {
    if (!periodEndIso || isLifetime) return null;
    const ms = new Date(periodEndIso).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    return Math.ceil(ms / 86400000);
  })();
  // "Ending soon" = active Premium that runs out within a week.
  const expiringSoon = isPaid && daysUntilEnd != null && daysUntilEnd >= 0 && daysUntilEnd <= 7;
  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(en ? 'en-US' : 'de-DE', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    } catch (e) { return ''; }
  };
  const fmtDateTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString(en ? 'en-US' : 'de-DE', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    } catch (e) { return iso; }
  };

  const planLabel = () => {
    const p = entitlement?.plan;
    if (p === 'monthly') return t('Monatlich', 'Monthly');
    if (p === 'yearly') return t('Jährlich', 'Yearly');
    if (p === 'lifetime') return t('Lifetime', 'Lifetime');
    return t('Premium', 'Premium');
  };
  const doCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      const res = await cancelSubscription();
      const endIso = res?.entitlement?.current_period_end || periodEndIso;
      if (endIso && !isLifetime) {
        toast.success(t(`Abo gekündigt. Premium bleibt bis ${fmtDate(endIso)} aktiv.`,
                        `Subscription cancelled. Premium stays active until ${fmtDate(endIso)}.`));
      } else {
        toast.success(t('Abo gekündigt.', 'Subscription cancelled.'));
      }
    } catch (e) {
      toast.error(t('Kündigung fehlgeschlagen.', 'Cancellation failed.'));
    } finally { setCancelling(false); }
  };
  const doReactivate = async () => {
    if (reactivating) return;
    setReactivating(true);
    try {
      await reactivateSubscription();
      toast.success(t('Kündigung rückgängig gemacht. Dein Abo verlängert sich wieder automatisch.',
                      'Cancellation undone. Your subscription will renew again automatically.'));
    } catch (e) {
      toast.error(t('Reaktivierung fehlgeschlagen.', 'Reactivation failed.'));
    } finally { setReactivating(false); }
  };
  const doScheduleDeletion = async () => {
    if (scheduling) return;
    setScheduling(true);
    try {
      const res = await api.scheduleAccountDeletion();
      if (res.data?.deleted) {
        toast.success(t('Konto gelöscht.', 'Account deleted.'));
        try { await logout(); } catch (e) { /* ignore */ }
        onClose();
        return;
      }
      toast.success(t('Löschung vorgemerkt – dein Konto wird nach Abo-Ablauf automatisch gelöscht.',
                      'Deletion scheduled — your account will be removed automatically after the subscription expires.'));
      try { await refresh(); } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error(t('Vormerkung fehlgeschlagen.', 'Could not schedule deletion.'));
    } finally { setScheduling(false); }
  };
  const doCancelSchedule = async () => {
    if (scheduling) return;
    setScheduling(true);
    try {
      await api.cancelScheduledDeletion();
      toast.success(t('Löschung-Vormerkung aufgehoben.', 'Scheduled deletion cancelled.'));
      try { await refresh(); } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error(t('Konnte Vormerkung nicht aufheben.', 'Could not cancel scheduled deletion.'));
    } finally { setScheduling(false); }
  };
  const downloadReceipt = async (p) => {
    try {
      const res = await api.paymentReceipt(p.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `barcode-studio-beleg-${String(p.id).slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('Beleg konnte nicht geladen werden.', 'Could not load receipt.'));
    }
  };

  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [username, setUsername] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [payments, setPayments] = useState([]);
  const [payLoading, setPayLoading] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  const [myDonations, setMyDonations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState(null);

  const loadSessions = useCallback(() => {
    setSessionsLoading(true);
    return api.listSessions()
      .then((r) => setSessions(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, []);

  const revokeOneSession = async (id) => {
    setRevokingSessionId(id);
    try {
      await api.revokeSession(id);
      toast.success(t('Gerät abgemeldet.', 'Device signed out.'));
      await loadSessions();
    } catch (e) {
      toast.error(t('Konnte Gerät nicht abmelden.', 'Could not sign out device.'));
    } finally { setRevokingSessionId(null); }
  };
  const [exportingHistory, setExportingHistory] = useState(false);
  const [licenseKey, setLicenseKey] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [revokingSessions, setRevokingSessions] = useState(false);

  const revokeOtherSessions = async () => {
    if (revokingSessions) return;
    setRevokingSessions(true);
    try {
      const res = await api.revokeOtherSessions();
      const n = res.data?.revoked ?? 0;
      toast.success(n > 0
        ? t(`${n} andere Sitzung(en) abgemeldet.`, `${n} other session(s) signed out.`)
        : t('Keine weiteren aktiven Sitzungen.', 'No other active sessions.'));
      await loadSessions();
    } catch (e) {
      toast.error(t('Konnte andere Sitzungen nicht abmelden.', 'Could not sign out other sessions.'));
    } finally { setRevokingSessions(false); }
  };

  const doRedeemLicense = async (e) => {
    if (e) e.preventDefault();
    const key = licenseKey.trim();
    if (!key || redeeming) return;
    setRedeeming(true);
    try {
      await redeemLicense(key);
      toast.success(t('Lizenz aktiviert – Premium ist freigeschaltet!', 'License activated – Premium unlocked!'));
      setLicenseKey('');
      try { await refresh(); } catch (e2) { /* ignore */ }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('Lizenz konnte nicht eingelöst werden.', 'Could not redeem license.'));
    } finally { setRedeeming(false); }
  };

  const downloadLoginHistory = async () => {
    if (exportingHistory) return;
    setExportingHistory(true);
    try {
      const res = await api.loginHistoryExport();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `login-verlauf-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('Export fehlgeschlagen.', 'Export failed.'));
    } finally { setExportingHistory(false); }
  };

  useEffect(() => {
    if (open) { setName(user?.name || ''); setUsername(user?.username || ''); setCurPw(''); setNewPw(''); }
  }, [open, user]);

  useEffect(() => {
    if (!open || !user) { setLoginHistory([]); setSessions([]); return; }
    let ok = true;
    setLoginHistoryLoading(true);
    api.loginHistory(20)
      .then((r) => { if (ok) setLoginHistory(Array.isArray(r.data?.items) ? r.data.items : []); })
      .catch(() => { if (ok) setLoginHistory([]); })
      .finally(() => { if (ok) setLoginHistoryLoading(false); });
    loadSessions();
    return () => { ok = false; };
  }, [open, user, loadSessions]);

  useEffect(() => {
    if (!open || !user) { setPayments([]); setLedger([]); return; }
    let ok = true;
    setPayLoading(true);
    api.paymentHistory()
      .then((r) => { if (ok) setPayments(Array.isArray(r.data?.payments) ? r.data.payments : []); })
      .catch(() => { if (ok) setPayments([]); })
      .finally(() => { if (ok) setPayLoading(false); });
    api.pointsHistory()
      .then((r) => { if (ok) setLedger(Array.isArray(r.data?.entries) ? r.data.entries : []); })
      .catch(() => { if (ok) setLedger([]); });
    api.getMyDonations()
      .then((r) => { if (ok) setMyDonations(Array.isArray(r.data?.items) ? r.data.items : []); })
      .catch(() => { if (ok) setMyDonations([]); });
    return () => { ok = false; };
  }, [open, user, isPaid, cancelling]);

  const ledgerLabel = (reason) => {
    if (reason === 'starter') return t('Startguthaben', 'Starter credit');
    if (reason === 'review_bonus') return t('Bewertungs-Bonus', 'Review bonus');
    if (reason === 'export' || reason === 'consume') return t('Export', 'Export');
    if (reason && reason.startsWith('plan_')) return t('Plan-Aufladung', 'Plan recharge');
    if (reason && reason.startsWith('points_')) return t('Punkte gekauft', 'Points purchased');
    if (reason === 'refund') return t('Erstattung', 'Refund');
    return reason || t('Änderung', 'Change');
  };

  const payPlanLabel = (p) => {
    if (p === 'monthly') return t('Monatlich', 'Monthly');
    if (p === 'yearly') return t('Jährlich', 'Yearly');
    if (p === 'lifetime') return t('Lifetime', 'Lifetime');
    return t('Premium', 'Premium');
  };
  const fmtAmount = (a, cur) => {
    if (a == null) return '—';
    try {
      return new Intl.NumberFormat(en ? 'en-US' : 'de-DE', { style: 'currency', currency: cur || 'EUR' }).format(a);
    } catch (e) { return `${a} ${cur || ''}`.trim(); }
  };

  const saveName = async (e) => {
    e.preventDefault();
    if (savingName) return;
    setSavingName(true);
    try {
      const res = await api.updateProfile({ name: name.trim() });
      updateUser(res.data.user);
      toast.success(t('Name aktualisiert.', 'Name updated.'));
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('Konnte nicht gespeichert werden.', 'Could not save.'));
    } finally { setSavingName(false); }
  };

  const { setUsername: setUsernameApi } = useAuth();
  const saveUsername = async (e) => {
    e.preventDefault();
    if (savingUsername) return;
    setSavingUsername(true);
    try {
      const res = await setUsernameApi(username.trim());
      if (res && res.user) updateUser(res.user);
      toast.success(t('Benutzername gespeichert.', 'Username saved.'));
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('Benutzername konnte nicht gespeichert werden.', 'Could not save username.'));
    } finally { setSavingUsername(false); }
  };

  const [savingPrefs, setSavingPrefs] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const avatarInputRef = React.useRef(null);

  const onAvatarPicked = (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';  // allow re-picking the same file
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error(t('Bitte wähle eine Bilddatei.', 'Please choose an image file.'));
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error(t('Bild ist zu groß (max. 6 MB).', 'Image is too large (max 6 MB).'));
      return;
    }
    setCropFile(file);  // open the crop/zoom dialog before uploading
  };

  const uploadCropped = async (blob) => {
    setAvatarBusy(true);
    try {
      const res = await api.uploadAvatar(blob);
      if (res.data && res.data.user) updateUser(res.data.user);
      setCropFile(null);
      toast.success(t('Profilbild aktualisiert.', 'Profile picture updated.'));
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('Bild konnte nicht hochgeladen werden.', 'Could not upload image.'));
    } finally { setAvatarBusy(false); }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    try {
      const res = await api.deleteAvatar();
      if (res.data && res.data.user) updateUser(res.data.user);
      toast.success(t('Profilbild entfernt.', 'Profile picture removed.'));
    } catch (err) {
      toast.error(t('Konnte nicht entfernt werden.', 'Could not remove.'));
    } finally { setAvatarBusy(false); }
  };

  const pickColor = async (c) => {
    try {
      const res = await api.setAvatarColor(c);
      if (res.data && res.data.user) updateUser(res.data.user);
    } catch (err) {
      toast.error(t('Farbe konnte nicht gesetzt werden.', 'Could not set color.'));
    }
  };

  const remindersOn = user?.email_reminders_enabled !== false;
  const toggleReminders = async (enabled) => {
    if (savingPrefs) return;
    setSavingPrefs(true);
    try {
      const res = await api.setEmailPrefs({ email_reminders_enabled: enabled });
      updateUser(res.data.user);
      toast.success(enabled
        ? t('Erinnerungs-E-Mails aktiviert.', 'Reminder emails enabled.')
        : t('Erinnerungs-E-Mails abbestellt.', 'Reminder emails unsubscribed.'));
    } catch (err) {
      toast.error(t('Konnte nicht gespeichert werden.', 'Could not save.'));
    } finally { setSavingPrefs(false); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    if (savingPw) return;
    if (newPw.length < 8) {
      toast.error(t('Das neue Passwort muss mindestens 8 Zeichen haben.', 'New password must be at least 8 characters.'));
      return;
    }
    setSavingPw(true);
    try {
      await api.changePassword({ current_password: curPw, new_password: newPw });
      setCurPw(''); setNewPw('');
      toast.success(t('Passwort geändert.', 'Password changed.'));
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('Passwortänderung fehlgeschlagen.', 'Password change failed.'));
    } finally { setSavingPw(false); }
  };

  const removeAccount = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.deleteAccount();
      toast.success(t('Konto gelöscht.', 'Account deleted.'));
      try { await logout(); } catch (e) { /* ignore */ }
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('Konto konnte nicht gelöscht werden.', 'Could not delete account.'));
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[88dvh] overflow-y-auto" data-testid="account-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{t('Konto-Einstellungen', 'Account settings')}</DialogTitle>
          <DialogDescription>{t('Verwalte dein Profil und deine Sicherheit.', 'Manage your profile and security.')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Account identity */}
          <div className="space-y-1.5">
            <Label>{t('E-Mail', 'Email')}</Label>
            <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="truncate" data-testid="account-email">{user?.email}</span>
              <Badge variant="secondary" className="ml-auto capitalize">
                {user?.auth_provider === 'google' ? 'Google' : t('E-Mail', 'Email')}
              </Badge>
            </div>
          </div>

          {/* Billing history / Rechnungs-Historie */}
          {user && (
            <div className="space-y-2 rounded-lg border p-3" data-testid="account-billing-history">
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{t('Rechnungs-Historie', 'Billing history')}</span>
              </div>
              {payLoading ? (
                <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />{t('Lade Zahlungen…', 'Loading payments…')}
                </div>
              ) : payments.length === 0 ? (
                <p className="py-1 text-xs text-muted-foreground" data-testid="account-billing-empty">
                  {t('Noch keine Zahlungen vorhanden.', 'No payments yet.')}
                </p>
              ) : (
                <ul className="divide-y divide-border" data-testid="account-billing-list">
                  {payments.map((p) => {
                    const paid = p.payment_status === 'paid';
                    return (
                      <li key={p.id} className="flex items-center justify-between gap-2 py-2 text-sm" data-testid={`billing-item-${p.id}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 font-medium">
                            {paid ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <Clock className="h-3.5 w-3.5 text-amber-500" />}
                            <span className="truncate">{payPlanLabel(p.plan)}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="font-medium tabular-nums">{fmtAmount(p.amount, p.currency)}</div>
                            <span className={`text-xs ${paid ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                              {paid ? t('Bezahlt', 'Paid') : t('Ausstehend', 'Pending')}
                            </span>
                          </div>
                          {paid && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                              onClick={() => downloadReceipt(p)} data-testid={`billing-receipt-${p.id}`}
                              title={t('PDF-Beleg herunterladen', 'Download PDF receipt')}
                              aria-label={t('PDF-Beleg herunterladen', 'Download PDF receipt')}>
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Points history / Punkte-Verlauf */}
          <PointsHistorySection user={user} ledger={ledger} ledgerLabel={ledgerLabel} fmtDate={fmtDate} t={t} />

          {myDonations.length > 0 ? (
            <div className="space-y-2" data-testid="account-donations">
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-muted-foreground" />
                <Label>{t('Spenden & Belege', 'Donations & receipts')}</Label>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t('Lade deine Spendenbelege jederzeit erneut als PDF herunter.',
                   'Re-download your donation receipts as a PDF anytime.')}
              </p>
              <ul className="divide-y divide-border rounded-lg border" data-testid="account-donations-list">
                {myDonations.map((d) => (
                  <li key={d.receipt_token} className="flex items-center justify-between gap-2 px-3 py-2.5"
                    data-testid={`account-donation-${d.receipt_token}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {typeof d.amount === 'number'
                          ? new Intl.NumberFormat(en ? 'en-US' : 'de-DE', { style: 'currency', currency: d.currency || 'USD' }).format(d.amount)
                          : '-'}
                        {d.is_sandbox ? (
                          <span className="ml-2 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600">{t('Test', 'Test')}</span>
                        ) : null}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {d.receipt_no ? `${d.receipt_no} · ` : ''}{fmtDate(d.date)}
                      </p>
                    </div>
                    <Button type="button" variant="outline" size="sm" className="h-8 shrink-0 gap-1.5" asChild
                      data-testid={`account-donation-download-${d.receipt_token}`}>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/donations/receipt/${d.receipt_token}`}
                        target="_blank" rel="noopener noreferrer">
                        <Download className="h-3.5 w-3.5" />{t('Beleg', 'Receipt')}
                      </a>
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex items-center justify-between gap-3 rounded-lg border p-3" data-testid="account-email-prefs">
            <div className="flex items-start gap-2.5">
              <Bell className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">{t('Erinnerungs-E-Mails', 'Reminder emails')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('Hinweise vor einer geplanten Kontol\u00f6schung. Wichtige E-Mails (z. B. Rechnungen) senden wir weiterhin.',
                     'Alerts before a scheduled account deletion. Essential emails (e.g. invoices) are still sent.')}
                </p>
              </div>
            </div>
            <Switch
              checked={remindersOn}
              onCheckedChange={toggleReminders}
              disabled={savingPrefs}
              aria-label={t('Erinnerungs-E-Mails', 'Reminder emails')}
              data-testid="account-email-reminders-switch"
            />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3" data-testid="account-security-prefs">
            <div className="flex items-start gap-2.5">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">{t('Sicherheits-E-Mails', 'Security emails')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('Warnungen bei Anmeldungen von einem neuen Ger\u00e4t oder Land \u2013 immer aktiv zu deinem Schutz.',
                     'Alerts for sign-ins from a new device or country \u2013 always on to keep your account safe.')}
                </p>
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary" data-testid="account-email-security-status">
              {t('Immer an', 'Always on')}
            </span>
          </div>
          <AvatarCropDialog
            file={cropFile}
            open={!!cropFile}
            busy={avatarBusy}
            onCancel={() => { if (!avatarBusy) setCropFile(null); }}
            onConfirm={uploadCropped}
          />

          <div className="rounded-lg border p-3" data-testid="account-avatar-section">
            <div className="flex items-center gap-4">
              <span className="inline-flex rounded-full" data-testid="account-avatar-wrap">
              {user?.picture ? (
                <img src={user.picture} alt={t('Profilbild', 'Profile picture')} className="h-16 w-16 rounded-full object-cover" data-testid="account-avatar-image" />
              ) : (
                <InitialsAvatar seed={user?.username || user?.name || user?.email || '?'} color={user?.avatar_color} size={64} />
              )}
              </span>
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium text-foreground">{t('Profilbild', 'Profile picture')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('JPG oder PNG, max. 6 MB. Ohne Bild zeigen wir deine Initialen.',
                     'JPG or PNG, max 6 MB. Without a picture we show your initials.')}
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={onAvatarPicked} data-testid="account-avatar-input" />
                  <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled={avatarBusy}
                    onClick={() => avatarInputRef.current && avatarInputRef.current.click()} data-testid="account-avatar-upload">
                    {avatarBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                    {t('Bild hochladen', 'Upload image')}
                  </Button>
                  {user?.picture ? (
                    <Button type="button" size="sm" variant="ghost" className="gap-1.5 text-destructive hover:text-destructive"
                      disabled={avatarBusy} onClick={removeAvatar} data-testid="account-avatar-remove">
                      <Trash2 className="h-4 w-4" />{t('Entfernen', 'Remove')}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            {!user?.picture && (
              <div className="mt-3 border-t pt-3" data-testid="account-avatar-colors">
                <p className="mb-2 text-xs font-medium text-muted-foreground">{t('Farbe der Initialen', 'Initials color')}</p>
                <div className="flex flex-wrap gap-2">
                  {PALETTE.map((c) => {
                    const active = (user?.avatar_color || '') === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => pickColor(c)}
                        aria-label={c}
                        aria-pressed={active}
                        data-testid={`avatar-color-${c}`}
                        className={`h-7 w-7 rounded-full ${c} transition-transform duration-150 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? 'ring-2 ring-offset-2 ring-foreground ring-offset-background' : ''}`}
                      />
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => pickColor('')}
                    aria-label={t('Automatisch', 'Automatic')}
                    data-testid="avatar-color-auto"
                    className={`inline-flex h-7 items-center rounded-full border border-border px-2.5 text-[11px] font-medium text-foreground transition-colors duration-150 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${!user?.avatar_color ? 'ring-2 ring-offset-2 ring-foreground ring-offset-background' : ''}`}
                  >
                    {t('Auto', 'Auto')}
                  </button>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={saveName} className="space-y-2">
            <Label htmlFor="acct-name">{t('Anzeigename', 'Display name')}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="acct-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder={t('Dein Name', 'Your name')} className="pl-9" data-testid="account-name-input" />
              </div>
              <Button type="submit" disabled={savingName} className="gap-1.5" data-testid="account-name-save">
                {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {t('Speichern', 'Save')}
              </Button>
            </div>
          </form>

          <form onSubmit={saveUsername} className="space-y-2">
            <Label htmlFor="acct-username">{t('Benutzername', 'Username')}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="acct-username" value={username} minLength={3} maxLength={30}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('z. B. max_mustermann', 'e.g. max_mustermann')} className="pl-9" data-testid="account-username-input" />
              </div>
              <Button type="submit" disabled={savingUsername} className="gap-1.5" data-testid="account-username-save">
                {savingUsername ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {t('Speichern', 'Save')}
              </Button>
            </div>
            <UsernameAvailability value={username} currentUsername={user?.username} onPick={(s) => setUsername(s)} />
            <p className="text-[11px] text-muted-foreground">
              {t('Damit kannst du dich auch ohne E-Mail anmelden. 3–30 Zeichen · Buchstaben, Zahlen, . _ -',
                 'Lets you sign in without your email. 3–30 chars · letters, digits, . _ -')}
            </p>
          </form>

          {isPasswordAccount && (
            <>
              <Separator />
              <form onSubmit={savePw} className="space-y-3">
                <Label>{t('Passwort ändern', 'Change password')}</Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} required
                    placeholder={t('Aktuelles Passwort', 'Current password')} className="pl-9" data-testid="account-current-password" autoComplete="current-password" />
                </div>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={8}
                    placeholder={t('Neues Passwort (min. 8 Zeichen)', 'New password (min. 8 chars)')} className="pl-9" data-testid="account-new-password" autoComplete="new-password" />
                </div>
                <Button type="submit" variant="outline" disabled={savingPw} className="w-full gap-1.5" data-testid="account-password-save">
                  {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                  {t('Passwort aktualisieren', 'Update password')}
                </Button>
              </form>
            </>
          )}

          <Separator />
          {/* Active sessions (devices currently signed in) */}
          <div className="space-y-2" data-testid="account-sessions">
            <div className="flex items-center gap-2">
              <MonitorSmartphone className="h-4 w-4 text-muted-foreground" />
              <Label>{t('Aktive Sitzungen', 'Active sessions')}</Label>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t('Geräte, die aktuell in deinem Konto angemeldet sind. Du kannst einzelne Geräte abmelden.',
                 'Devices currently signed in to your account. You can sign out individual devices.')}
            </p>
            {sessionsLoading ? (
              <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground" data-testid="account-sessions-loading">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />{t('Lade…', 'Loading…')}
              </div>
            ) : sessions.length === 0 ? (
              <p className="py-1 text-xs text-muted-foreground" data-testid="account-sessions-empty">
                {t('Keine aktiven Sitzungen.', 'No active sessions.')}
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border" data-testid="account-sessions-list">
                {sessions.map((s) => (
                  <li key={s.id}
                    className={`flex items-center justify-between gap-2 px-3 py-2 text-xs ${s.suspicious ? 'bg-destructive/10' : s.current ? 'bg-primary/5' : ''}`}
                    data-testid="account-session-item">
                    <div className="flex min-w-0 items-center gap-2">
                      <MonitorSmartphone className={`h-4 w-4 shrink-0 ${s.suspicious ? 'text-destructive' : s.current ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-1.5 truncate font-medium text-foreground">
                          {s.device || t('Unbekanntes Gerät', 'Unknown device')}
                          {s.current ? (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary" data-testid="account-session-current">
                              <CheckCircle2 className="h-2.5 w-2.5" />{t('Dieses Gerät', 'This device')}
                            </span>
                          ) : null}
                          {s.suspicious ? (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-destructive/15 px-1.5 py-0.5 text-[10px] font-semibold text-destructive" data-testid="account-session-suspicious">
                              <AlertTriangle className="h-2.5 w-2.5" />{t('Ungewöhnliches Land', 'Unusual country')}
                            </span>
                          ) : null}
                        </p>
                        <p className="flex items-center gap-1 truncate text-muted-foreground">
                          <MapPin className="h-3 w-3 shrink-0" />
                          {s.location ? `${s.location} · ` : ''}{s.ip || '—'}
                        </p>
                        <p className="truncate text-[11px] text-muted-foreground">
                          {t('Zuletzt aktiv', 'Last active')}: {fmtDateTime(s.last_seen || s.created_at)}
                        </p>
                      </div>
                    </div>
                    {s.current ? (
                      <span className="shrink-0 text-[11px] text-muted-foreground">{t('aktiv', 'active')}</span>
                    ) : (
                      <Button type="button" variant={s.suspicious ? 'destructive' : 'outline'} size="sm" className="h-7 shrink-0 gap-1 text-[11px]"
                        onClick={() => revokeOneSession(s.id)} disabled={revokingSessionId === s.id}
                        data-testid="account-session-revoke">
                        {revokingSessionId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                        {s.suspicious ? t('Sofort abmelden', 'Sign out now') : t('Abmelden', 'Sign out')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Separator />
          {/* Login history (last successful sign-ins) */}
          <div className="space-y-2" data-testid="account-login-history">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />
                <Label>{t('Login-Verlauf', 'Login history')}</Label>
              </div>
              <Button type="button" variant="ghost" size="sm" className="h-7 gap-1.5 text-xs"
                onClick={downloadLoginHistory} disabled={exportingHistory || loginHistory.length === 0}
                data-testid="account-login-history-export">
                {exportingHistory ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {t('Exportieren', 'Export')}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t('Deine letzten erfolgreichen Anmeldungen. Erkennst du etwas nicht, ändere dein Passwort.',
                 'Your most recent successful sign-ins. If something looks unfamiliar, change your password.')}
            </p>
            {loginHistory.some((h) => h.country_code) ? (
              <LoginMap items={loginHistory} />
            ) : null}
            {loginHistoryLoading ? (
              <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground" data-testid="account-login-history-loading">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />{t('Lade…', 'Loading…')}
              </div>
            ) : loginHistory.length === 0 ? (
              <p className="py-1 text-xs text-muted-foreground" data-testid="account-login-history-empty">
                {t('Noch keine Anmeldungen protokolliert.', 'No sign-ins recorded yet.')}
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border" data-testid="account-login-history-list">
                {loginHistory.map((h) => (
                  <li key={h.id}
                    className={`flex items-center justify-between gap-2 px-3 py-2 text-xs ${h.is_new ? 'bg-amber-500/10' : ''}`}
                    data-testid="account-login-history-item">
                    <div className="flex min-w-0 items-center gap-2">
                      <MonitorSmartphone className={`h-4 w-4 shrink-0 ${h.is_new ? 'text-amber-600' : 'text-muted-foreground'}`} />
                      <div className="min-w-0">
                        <p className="flex items-center gap-1.5 truncate font-medium text-foreground">
                          {h.device || t('Unbekanntes Gerät', 'Unknown device')}
                          {h.is_new ? (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid="account-login-new-badge">
                              <AlertTriangle className="h-2.5 w-2.5" />{t('Neu', 'New')}
                            </span>
                          ) : null}
                        </p>
                        <p className="flex items-center gap-1 truncate text-muted-foreground">
                          <MapPin className="h-3 w-3 shrink-0" />{h.ip || '—'}
                          {h.country ? (
                            <span className="ml-0.5">
                              {h.country_code ? `${flagEmoji(h.country_code)} ` : ''}{h.country}
                            </span>
                          ) : null}
                          {h.provider === 'google' ? ' · Google' : ''}
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 text-right text-[11px] text-muted-foreground">{fmtDateTime(h.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
            {loginHistory.length > 0 ? (
              <Button type="button" variant="outline" size="sm" className="mt-1 w-full gap-1.5 text-xs"
                onClick={revokeOtherSessions} disabled={revokingSessions} data-testid="account-revoke-sessions">
                {revokingSessions ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                {t('Alle anderen Geräte abmelden', 'Sign out all other devices')}
              </Button>
            ) : null}
          </div>

          <Separator />
          {/* Danger zone */}
          <div className="space-y-2">
            <Label className="text-destructive">{t('Konto löschen', 'Delete account')}</Label>
            {isPaid ? (
              <div className="space-y-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="account-delete-blocked">
                <div className="flex items-start gap-2">
                  <Crown className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  <p className="text-xs text-muted-foreground">
                    {cancelAtPeriodEnd
                      ? (periodEndIso
                          ? t(`Dein Abo wurde gekündigt und endet am ${fmtDate(periodEndIso)}. Erst nach Ablauf kannst du dein Konto löschen.`,
                               `Your subscription is cancelled and ends on ${fmtDate(periodEndIso)}. You can delete your account only after it expires.`)
                          : t('Dein Abo wurde gekündigt. Erst nach Ablauf kannst du dein Konto löschen.',
                               'Your subscription is cancelled. You can delete your account only after it expires.'))
                      : (periodEndIso
                          ? t(`Du hast ein aktives Abo (läuft bis ${fmtDate(periodEndIso)}). Bitte kündige zuerst – nach Ablauf kannst du dein Konto löschen.`,
                               `You have an active subscription (until ${fmtDate(periodEndIso)}). Please cancel first — you can delete your account after it expires.`)
                          : t('Du hast ein aktives Abo. Bitte kündige zuerst – nach Ablauf des Abos kannst du dein Konto löschen.',
                               'You have an active subscription. Please cancel first — you can delete your account after it expires.'))}
                  </p>
                </div>
                {daysUntilEnd != null && daysUntilEnd >= 0 && (
                  <div className="flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400" data-testid="account-delete-countdown">
                    <CalendarClock className="h-3.5 w-3.5" />
                    {daysUntilEnd === 0
                      ? t('Abo endet heute.', 'Subscription ends today.')
                      : t(`Noch ${daysUntilEnd} Tag(e) bis zum Ablauf.`, `${daysUntilEnd} day(s) until it expires.`)}
                  </div>
                )}
                {!cancelAtPeriodEnd ? (
                  <Button variant="outline"
                    className="w-full gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={doCancel} disabled={cancelling} data-testid="account-delete-cancel-sub">
                    {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    {t('Abo kündigen', 'Cancel subscription')}
                  </Button>
                ) : (
                  <Button variant="outline"
                    className="w-full gap-1.5 border-primary/40 text-primary hover:bg-primary/10 hover:text-primary"
                    onClick={doReactivate} disabled={reactivating} data-testid="account-delete-reactivate">
                    {reactivating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                    {t('Kündigung rückgängig machen', 'Undo cancellation')}
                  </Button>
                )}
                {/* Schedule deletion for after expiry */}
                {pendingDeletion ? (
                  <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-2.5" data-testid="account-deletion-scheduled">
                    <p className="text-xs text-muted-foreground">
                      {t('Löschung vorgemerkt: Dein Konto wird nach Abo-Ablauf automatisch gelöscht.',
                         'Deletion scheduled: your account will be removed automatically after the subscription expires.')}
                    </p>
                    <Button variant="ghost" size="sm" className="w-full gap-1.5"
                      onClick={doCancelSchedule} disabled={scheduling} data-testid="account-cancel-schedule">
                      {scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                      {t('Vormerkung aufheben', 'Cancel scheduled deletion')}
                    </Button>
                  </div>
                ) : (
                  <Button variant="ghost" size="sm"
                    className="w-full gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={doScheduleDeletion} disabled={scheduling} data-testid="account-schedule-delete">
                    {scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    {t('Löschung nach Ablauf vormerken', 'Schedule deletion after expiry')}
                  </Button>
                )}
              </div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  {t('Löscht dein Konto und alle Sitzungen dauerhaft. Dies kann nicht rückgängig gemacht werden.',
                     'Permanently deletes your account and all sessions. This cannot be undone.')}
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="w-full gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid="account-delete-btn">
                      <Trash2 className="h-4 w-4" />{t('Konto löschen', 'Delete account')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="account-delete-confirm-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('Konto wirklich löschen?', 'Delete account?')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('Alle deine Daten werden dauerhaft entfernt. Dieser Vorgang kann nicht rückgängig gemacht werden.',
                           'All your data will be permanently removed. This action cannot be undone.')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('Abbrechen', 'Cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={removeAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="account-delete-confirm">
                        {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        {t('Endgültig löschen', 'Delete permanently')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountSettingsDialog;
